import { useQuery } from "@tanstack/react-query";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import api from "../../../../datasource/api";
import dayjs from "dayjs";
import React from "react";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function EquipmentDetailsChart({ hospitalid }: { hospitalid: string }) {

    const getAllEquipments = async () => {
        const singleEquipments = await api.get('/CMMIS/ccmis/hospitalequimentsingle', {
            params: {
                hospitalid,
                fromdate: dayjs().startOf("day").toISOString(),
                todate: dayjs().endOf("day").toISOString()
            }
        });

        const multipleEquipments = await api.get('/CMMIS/ccmis/hospitalequimentmultiple', {
            params: {
                hospitalid,
                fromdate: dayjs().startOf("day").toISOString(),
                todate: dayjs().endOf("day").toISOString()
            }
        });

        return [
            ...singleEquipments.data?.map((v: any) => ({ ...v, isSingle: true })),
            ...multipleEquipments.data
        ];
    };

    const { data: equimentData } = useQuery({
        queryKey: ['equipmentslist',hospitalid],
        queryFn: getAllEquipments
    });

    // ✅ Transform API → Chart Data
    const { chartData, maxValue, totalEquipments } = React.useMemo(() => {
        if (!equimentData) {
            return { chartData: null, maxValue: 0, totalEquipments: 0 };
        }

        const labels: string[] = [];
        const usedData: number[] = [];
        const bookedData: number[] = [];
        const unusedData: number[] = [];

        let totalEquipments = 0;

        equimentData.forEach((item: any) => {
            labels.push(item.hospitalequipmenttypeDescription?.trim() || "Unknown");

            totalEquipments += (item.totalCount || 0) - (item.totalUMCount || 0);

            const isHoursType = item.totalAvailableHours !== undefined;

            let used = 0;
            let booked = 0;
            let unused = 0;

            if (isHoursType) {
                // Hours-based (Dialyser)
                used = item.totalUsedCount || 0;
                booked = item.totalBookedCount || 0;
                unused = item.totalUnUsedCount || 0;
            } else {
                // Count-based (Ventilator, Phototherapy)
                used =
                    (item.usedAllocatedTotalCount || 0) +
                    (item.usedUnAllocatedTotalCount || 0);

                booked =
                    (item.bookedAllocatedTotalCount || 0) +
                    (item.bookedUnAllocatedTotalCount || 0);

                unused =
                    (item.totalVacantCount || 0);
            }

            usedData.push(used);
            bookedData.push(booked);
            unusedData.push(unused);
        });

        // ✅ Dynamic max for X-axis
        const totals = usedData.map((v, i) => v + bookedData[i] + unusedData[i]);
        const maxValue = Math.ceil(Math.max(...totals, 0) / 10) * 10;

        return {
            chartData: {
                labels,
                datasets: [
                    {
                        label: "Used",
                        data: usedData,
                        backgroundColor: "#8EE6C9",
                        borderColor: "#FFFFFF",
                        borderWidth: 1.5,
                        borderSkipped: false,
                        barThickness: 22,
                    },
                    {
                        label: "Booked",
                        data: bookedData,
                        backgroundColor: "#F9C36B",
                        borderColor: "#FFFFFF",
                        borderWidth: 1.5,
                        borderSkipped: false,
                        barThickness: 22,
                    },
                    {
                        label: "Unused",
                        data: unusedData,
                        backgroundColor: "#E97787",
                        borderColor: "#FFFFFF",
                        borderWidth: 1.5,
                        borderSkipped: false,
                        barThickness: 22,
                    },
                ],
            },
            maxValue,
            totalEquipments
        };
    }, [equimentData]);

    const options = {
        indexAxis: "y" as const,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                left: 20,
                right: 20,
                top: 10,
                bottom: 10,
            },
        },
        scales: {
            x: {
                stacked: true,
                min: 0,
                max: maxValue || 10, // ✅ dynamic max
                ticks: {
                    stepSize: Math.ceil((maxValue || 10) / 5), // ✅ dynamic steps
                    color: "#6B7280",
                    font: { size: 13 },
                },
                grid: {
                    display: true,
                    color: "#EDF1F7",
                    lineWidth: 1,
                },
                border: { display: false },
            },
            y: {
                stacked: true,
                ticks: {
                    color: "#6B7280",
                    font: { size: 14 },
                },
                grid: {
                    display: true,
                    color: "#EDF1F7",
                    lineWidth: 1,
                },
                border: { display: false },
                categoryPercentage: 0.72,
                barPercentage: 0.95,
            },
        },
        plugins: {
            legend: {
                position: "bottom" as const,
                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    padding: 30,
                    boxWidth: 10,
                    color: "#111827",
                    font: { size: 14 },
                },
            },
            tooltip: {
                enabled: true,
                backgroundColor: "#FFFFFF",
                titleColor: "#111827",
                bodyColor: "#374151",
                borderColor: "#E5E7EB",
                borderWidth: 1,
                cornerRadius: 8,
                padding: 10,
                displayColors: false,
                callbacks: {
                    title: (items: any) => items[0].label,
                    label: (item: any) => `${item.dataset.label}: ${item.raw}`,
                },
            },
        },
    };

    return (
        <div
            style={{
                background: "#FFFFFF",
                borderRadius: 12,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                width: "100%"
            }}
            className="p-4"
        >
            <span className="text-18 font-medium text-primary mx-2">
                Equipment Details
            </span>

            <p className="mx-2 pt-2" style={{ color: "#6B7280", fontSize: 14 }}>
                No. of Equipment's:{" "}
                <strong style={{ color: "#111827" }}>
                    {totalEquipments || 0}
                </strong>
            </p>

            <div style={{ height: 280 }}>
                {chartData && <Bar data={chartData} options={options} />}
            </div>
        </div>
    );
}